import {
  cellChance,
  DETAIL_PATCH,
  DETAIL_PIECE_IDS,
  DETAIL_PIECES,
  GROUND_DETAILS,
  POI_RULE,
  RESOURCE_TYPES,
  type BiomeName,
  type DetailPiece,
  type GroundDetailId,
  type GroundDetailRule,
} from "@eldrun/content";
import { datan2, dcos, deriveSeed, dsin, hash32, toUnitFloat } from "@eldrun/shared";
import {
  BIOME_NAMES,
  CHUNK_SIZE,
  CHUNKS_PER_SIDE,
  classifyBiome,
  sampleGradient,
  sampleHeight,
  samplePlanGrid,
  WORLD_HALF,
  type Heightfield,
  type Poi,
  type ResourceGenerator,
} from "@eldrun/worldgen";

/** Subcélula (m) em que o worker agrupa as instâncias: o cliente copia subcélulas inteiras. */
export const DETAIL_SUBCELL = 32;
/** Subcélulas por lado do chunk. */
export const SUBCELLS = CHUNK_SIZE / DETAIL_SUBCELL;
export const PIECE_COUNT = DETAIL_PIECE_IDS.length;
/** Grupos de um chunk: subcélula (`sz · 4 + sx`) × peça. */
export const DETAIL_GROUPS = SUBCELLS * SUBCELLS * PIECE_COUNT;

/** Grupo de uma peça numa subcélula do chunk. */
export const groupOf = (sub: number, piece: number): number => sub * PIECE_COUNT + piece;

/**
 * Enfeites de um chunk, prontos para o desenho: as matrizes (16 floats cada, coluna a coluna como no
 * three) agrupadas por subcélula de 32 m e peça. O grupo `g` vai de `offsets[g]` a `offsets[g + 1]`,
 * em ordem crescente de `rank` (o corte de densidade copia o começo de cada grupo). `biome` escolhe a
 * paleta das mudas; `tint`, o tom (±8 %).
 */
export interface DetailChunk {
  cx: number;
  cz: number;
  count: number;
  offsets: Uint32Array;
  matrices: Float32Array;
  rank: Uint8Array;
  biome: Uint8Array;
  tint: Uint8Array;
}

export function detailTransferables(chunk: DetailChunk): ArrayBuffer[] {
  return [chunk.offsets, chunk.matrices, chunk.rank, chunk.biome, chunk.tint].map((a) => a.buffer as ArrayBuffer);
}

/** O que o espalhamento lê do plano: as grades e os POIs (cópias do worker, nunca alteradas). */
export interface DetailPlan {
  seed: number;
  moisture: Float32Array;
  coastDistance: Float32Array;
  landDistance: Float32Array;
  pois: readonly Poi[];
}

/** Folga (m) além da pegada dos POIs: o chão carimbado fica limpo, com uma borda vazia aceitável. */
export const POI_MARGIN = 2;
/** Folga (m) entre um enfeite e o corpo de um recurso. */
export const RESOURCE_MARGIN = 0.2;
/**
 * Altura (m, escala 1 do recurso) até onde o corpo dele conta: a muda mais alta (1,2 m) sob o recurso
 * na menor escala (0,8).
 */
export const FOOTPRINT_BAND = 1.5;

/**
 * Corpo visível de cada recurso no chão (m, escala 1): a maior distância ao eixo dos vértices até
 * `FOOTPRINT_BAND`, entre as variantes de modelo e a malha procedural de reserva (a que o jogo desenha
 * sem os pacotes). Não é o colisor: o barreiro e a moita não têm, a copa chorona da Amoreira desce
 * perto do chão, os galhos baixos do Pinheiro e da Pereira abrem e as rochas passam do colisor. Medido
 * nas malhas; `detail-scatter.test.ts` confere que cobre e não sobra.
 */
export const RESOURCE_FOOTPRINT: Readonly<Record<string, number>> = {
  galhada: 0.75,
  moita: 0.4,
  bananeira: 1.15,
  embauba: 0.3,
  cajueiro: 0.5,
  coqueiro: 1.55,
  acaizeiro: 2.3,
  ipe: 0.65,
  jatoba: 0.55,
  angelim: 0.95,
  rocha: 1.3,
  barreiro: 1.6,
  "veio-carvao": 1.2,
  "veio-ferro": 1.15,
  "veio-prata": 1.1,
  cardume: 1.1,
};

/** Maior corpo (m) de recurso, na maior escala: até onde a busca de vizinhos olha. */
const MAX_BODY = Math.max(...RESOURCE_TYPES.map((t) => (RESOURCE_FOOTPRINT[t.id] ?? 0) * t.scale[1]));

/** Raio (m) do corpo de um recurso para os enfeites: o corpo visível no chão vezes a escala. */
export function resourceBodyRadius(typeIndex: number, scale: number): number {
  const type = RESOURCE_TYPES[typeIndex];
  return (type ? (RESOURCE_FOOTPRINT[type.id] ?? MAX_BODY) : MAX_BODY) * scale;
}

/**
 * Enfeites grandes que os outros evitam (uma muda não atravessa um tronco, uma pedrinha não fura a
 * laje). Entre eles, vale o de índice menor: a laje tira o tronco.
 */
const BLOCKING: ReadonlySet<GroundDetailId> = new Set(["laje", "tronco"]);

/** Assentamento das peças compridas e da laje: o fundo no máximo isto (m) acima do chão em todo ponto. */
export const DETAIL_MAX_FLOAT = 0.03;
/** ...e pelo menos esta fração da altura da peça à vista em todo ponto (nada some no meio). */
export const DETAIL_MIN_SHOW = 0.4;
/** Passo (m) das amostras do chão sob as peças assentadas pela área. */
const SEAT_STEP = 0.25;

/** Fração da célula usada como margem (candidatos de células vizinhas não encostam). */
const MARGIN = 0.1;
const TAU = 6.283185307179586;
/** Declive abaixo do qual a curva de nível não tem rumo: o tronco gira à toa. */
const FLAT = 0.03;
/** Passo (m) das amostras ao longo das peças compridas (contra peças compridas dos vizinhos). */
const SEGMENT_STEP = 0.3;

/** Forma no chão: o segmento de `a` a `b` (um ponto, nas peças redondas) engrossado por `r`. */
interface Shape {
  ax: number;
  az: number;
  bx: number;
  bz: number;
  r: number;
}

interface Placed extends Shape {
  piece: number;
  sub: number;
  rank: number;
  biome: number;
  tint: number;
  m: number[];
}

/** Ponto do chão que passou nas regras do lugar, com o bioma e o gradiente dele. */
interface Site {
  y: number;
  gx: number;
  gz: number;
  biome: number;
}

const PIECE_INDEX = new Map<DetailPiece, number>(DETAIL_PIECE_IDS.map((p, i) => [p, i]));

/** Distância² de (px, pz) ao segmento a–b. */
function pointSegment2(px: number, pz: number, s: Shape): number {
  const dx = s.bx - s.ax;
  const dz = s.bz - s.az;
  const len2 = dx * dx + dz * dz;
  const t = len2 > 0 ? Math.max(0, Math.min(1, ((px - s.ax) * dx + (pz - s.az) * dz) / len2)) : 0;
  const ex = s.ax + dx * t - px;
  const ez = s.az + dz * t - pz;
  return ex * ex + ez * ez;
}

/** As duas formas se tocam? Amostra o segmento de `a` (peças compridas) contra o de `b`. */
function touches(a: Shape, b: Shape): boolean {
  const reach = a.r + b.r;
  const len = Math.hypot(a.bx - a.ax, a.bz - a.az);
  const steps = Math.max(1, Math.ceil(len / SEGMENT_STEP));
  for (let k = 0; k <= steps; k++) {
    const t = k / steps;
    if (pointSegment2(a.ax + (a.bx - a.ax) * t, a.az + (a.bz - a.az) * t, b) < reach * reach) return true;
  }
  return false;
}

/** Pegadas dos POIs com a folga, num índice de 128 m. */
class PoiClearance {
  private static readonly SIDE = CHUNKS_PER_SIDE;
  private readonly cells: { x: number; z: number; r2: number }[][];

  constructor(pois: readonly Poi[], margin: number) {
    this.cells = Array.from({ length: PoiClearance.SIDE * PoiClearance.SIDE }, () => []);
    for (const p of pois) {
      const footprint = POI_RULE.get(p.type)?.footprint ?? 0;
      if (footprint <= 0) continue;
      const r = footprint + margin;
      for (let j = this.cell(p.z - r); j <= this.cell(p.z + r); j++) {
        for (let i = this.cell(p.x - r); i <= this.cell(p.x + r); i++) this.cells[j * PoiClearance.SIDE + i]!.push({ x: p.x, z: p.z, r2: r * r });
      }
    }
  }

  private cell(v: number): number {
    return Math.min(PoiClearance.SIDE - 1, Math.max(0, Math.floor((v + WORLD_HALF) / CHUNK_SIZE)));
  }

  inside(x: number, z: number): boolean {
    for (const f of this.cells[this.cell(z) * PoiClearance.SIDE + this.cell(x)]!) {
      const dx = x - f.x;
      const dz = z - f.z;
      if (dx * dx + dz * dz < f.r2) return true;
    }
    return false;
  }
}

/** Balde (m) do índice dos corpos dos recursos. */
const BODY_BUCKET = 8;
const BODY_SIDE = (2 * WORLD_HALF) / BODY_BUCKET;
const bucketOf = (v: number) => Math.min(BODY_SIDE - 1, Math.max(0, Math.floor((v + WORLD_HALF) / BODY_BUCKET)));

/**
 * Espalhamento dos enfeites do chão (F44), puro e testável em Node: roda no worker de terreno, que já
 * tem o plano, o heightfield inteiro e o gerador de recursos. Cada regra decide por célula com um hash
 * sem estado de (seed, regra, célula) e as células dividem o chunk, então o resultado não depende da
 * ordem dos chunks nem do worker. Só lê as cópias do worker: mundo, recursos e hashes não mudam.
 *
 * Um candidato passa pelo lugar (altura acima da água, fora dos POIs com folga, declive, bioma, a
 * praia das tábuas), pela densidade do bioma vezes a mancha de 16 m, e pela forma no chão: longe dos
 * corpos dos recursos dos chunks vizinhos e das lajes e troncos dos vizinhos. As peças compridas
 * assentam pelas duas pontas; as de chão seguem a normal; as mudas ficam em pé.
 */
export class DetailScatter {
  private readonly plan: DetailPlan;
  private readonly heightfield: Heightfield;
  private readonly generator: ResourceGenerator;
  private readonly seeds: number[];
  private readonly patchSeeds: number[];
  /** Maior chance por célula sem a mancha (o corte rápido antes de olhar o terreno). */
  private readonly maxChance: number[];
  private readonly clearance: PoiClearance;
  /** POIs perto dos quais cada regra de praia vale (Destroços e Costa de chegada para as tábuas). */
  private readonly shoreAnchors: ({ x: number; z: number }[] | undefined)[];
  /** Chunks de recursos já gerados e os corpos deles, por balde de 8 m. */
  private readonly resourceChunks = new Set<number>();
  private readonly bodies = new Map<number, { x: number; z: number; r: number }[]>();
  /** Candidatos das regras que bloqueiam (laje, tronco), por célula. */
  private readonly blockers = new Map<number, Placed | null>();
  private readonly gradient = { x: 0, z: 0 };

  constructor(plan: DetailPlan, heightfield: Heightfield, generator: ResourceGenerator) {
    this.plan = plan;
    this.heightfield = heightfield;
    this.generator = generator;
    this.seeds = GROUND_DETAILS.map((r) => deriveSeed(plan.seed, "detalhes", r.index));
    this.patchSeeds = GROUND_DETAILS.map((r) => deriveSeed(plan.seed, "detalhes-manchas", r.index));
    this.maxChance = GROUND_DETAILS.map((r) => Math.max(...BIOME_NAMES.map((b) => cellChance(r, b))));
    this.clearance = new PoiClearance(plan.pois, POI_MARGIN);
    this.shoreAnchors = GROUND_DETAILS.map((r) => (r.shore ? plan.pois.filter((p) => r.shore!.near.includes(p.type)).map((p) => ({ x: p.x, z: p.z })) : undefined));
  }

  /** Enfeites cujo candidato cai no chunk (cx, cz), agrupados por subcélula e peça. */
  chunk(cx: number, cz: number): DetailChunk {
    const groups: Placed[][] = Array.from({ length: DETAIL_GROUPS }, () => []);
    for (const rule of GROUND_DETAILS) {
      const perChunk = CHUNK_SIZE / rule.cell;
      for (let dj = 0; dj < perChunk; dj++) {
        for (let di = 0; di < perChunk; di++) {
          for (const p of this.cell(rule, cx * perChunk + di, cz * perChunk + dj)) groups[groupOf(p.sub, p.piece)]!.push(p);
        }
      }
    }
    let count = 0;
    for (const g of groups) count += g.length;
    const out: DetailChunk = {
      cx,
      cz,
      count,
      offsets: new Uint32Array(DETAIL_GROUPS + 1),
      matrices: new Float32Array(count * 16),
      rank: new Uint8Array(count),
      biome: new Uint8Array(count),
      tint: new Uint8Array(count),
    };
    let k = 0;
    groups.forEach((list, g) => {
      out.offsets[g] = k;
      // Estável: empates mantêm a ordem das regras e das células.
      list.sort((a, b) => a.rank - b.rank);
      for (const p of list) {
        out.matrices.set(p.m, k * 16);
        out.rank[k] = p.rank;
        out.biome[k] = p.biome;
        out.tint[k] = p.tint;
        k++;
      }
    });
    out.offsets[DETAIL_GROUPS] = k;
    return out;
  }

  /**
   * O lugar serve para a regra? Altura acima da água (por bioma), fora dos POIs com folga, declive,
   * bioma com densidade e, nas tábuas, a praia perto do mar e dos Destroços ou da Costa de chegada.
   * Sem os recursos nem os outros enfeites (exclusões de forma). Os testes medem a densidade sobre isto.
   */
  site(rule: GroundDetailRule, x: number, z: number): Site | undefined {
    const y = sampleHeight(this.heightfield, x, z);
    if (y < rule.minY) return undefined;
    if (this.clearance.inside(x, z)) return undefined;
    const coastDistance = samplePlanGrid(this.plan.coastDistance, x, z);
    if (rule.shore) {
      if (coastDistance >= rule.shore.coast) return undefined;
      const r2 = rule.shore.radius * rule.shore.radius;
      if (!this.shoreAnchors[rule.index]!.some((a) => (a.x - x) ** 2 + (a.z - z) ** 2 <= r2)) return undefined;
    }
    const g = sampleGradient(this.heightfield, x, z, this.gradient);
    const slope = Math.sqrt(g.x * g.x + g.z * g.z);
    if (slope > rule.maxSlope) return undefined;
    const biome = classifyBiome({ y, moisture: samplePlanGrid(this.plan.moisture, x, z), slope, coastDistance, landDistance: samplePlanGrid(this.plan.landDistance, x, z) });
    const name = BIOME_NAMES[biome] as BiomeName;
    if (!(rule.density[name] ?? 0)) return undefined;
    if (y < (rule.minYBy?.[name] ?? rule.minY)) return undefined;
    return { y, gx: g.x, gz: g.z, biome };
  }

  /** Mancha da regra em (x, z): ruído de valor suave numa grade de 16 m, de 0,3 a 1,7 (média 1). */
  patch(rule: GroundDetailRule, x: number, z: number): number {
    const seed = this.patchSeeds[rule.index]!;
    const gx = (x + WORLD_HALF) / DETAIL_PATCH.cell;
    const gz = (z + WORLD_HALF) / DETAIL_PATCH.cell;
    const i = Math.floor(gx);
    const j = Math.floor(gz);
    const fx = gx - i;
    const fz = gz - j;
    const sx = fx * fx * (3 - 2 * fx);
    const sz = fz * fz * (3 - 2 * fz);
    const v = (a: number, b: number) => toUnitFloat(hash32(seed, a, b));
    const top = v(i, j) + (v(i + 1, j) - v(i, j)) * sx;
    const bottom = v(i, j + 1) + (v(i + 1, j + 1) - v(i, j + 1)) * sx;
    return DETAIL_PATCH.min + (DETAIL_PATCH.max - DETAIL_PATCH.min) * (top + (bottom - top) * sz);
  }

  /** Enfeites de uma célula da regra (nenhum, um, ou o grupo das tábuas). */
  private cell(rule: GroundDetailRule, i: number, j: number): Placed[] {
    if (BLOCKING.has(rule.id)) {
      const p = this.blocker(rule, i, j);
      return p ? [p] : [];
    }
    return this.evaluate(rule, i, j);
  }

  /** Candidato de uma regra que bloqueia, com cache (os vizinhos o consultam). */
  private blocker(rule: GroundDetailRule, i: number, j: number): Placed | null {
    const key = rule.index * 16777216 + i * 4096 + j;
    const cached = this.blockers.get(key);
    if (cached !== undefined) return cached;
    const value = this.evaluate(rule, i, j)[0] ?? null;
    this.blockers.set(key, value);
    return value;
  }

  private evaluate(rule: GroundDetailRule, i: number, j: number): Placed[] {
    const seed = this.seeds[rule.index]!;
    const u = (k: number) => toUnitFloat(hash32(seed, i, j, k));
    const u0 = u(0);
    // Corte rápido antes de olhar o terreno: nem com a mancha mais forte a célula teria candidato.
    if (u0 >= this.maxChance[rule.index]! * DETAIL_PATCH.max) return [];
    const c = rule.cell;
    const margin = c * MARGIN;
    const x = -WORLD_HALF + i * c + margin + (c - 2 * margin) * u(1);
    const z = -WORLD_HALF + j * c + margin + (c - 2 * margin) * u(2);
    const patch = this.patch(rule, x, z);
    if (u0 >= this.maxChance[rule.index]! * patch) return [];
    const site = this.site(rule, x, z);
    if (!site) return [];
    const biome = BIOME_NAMES[site.biome] as BiomeName;
    if (u0 >= cellChance(rule, biome) * patch) return [];
    const piece = rule.pieces.length > 1 && u(3) < (rule.mix?.[biome] ?? 0) ? rule.pieces[1]! : rule.pieces[0];
    const rank = Math.floor(u(4) * 256);
    // Subcélula do candidato (as peças do grupo ficam com ela, mesmo saindo um pouco dela).
    const sub = Math.floor(((x + WORLD_HALF) % CHUNK_SIZE) / DETAIL_SUBCELL) + SUBCELLS * Math.floor(((z + WORLD_HALF) % CHUNK_SIZE) / DETAIL_SUBCELL);
    const yaw = this.heading(rule, site, u(5), u(6));
    const members = rule.group ? rule.group[0] + Math.min(rule.group[1] - rule.group[0], Math.floor(u(7) * (rule.group[1] - rule.group[0] + 1))) : 1;
    const out: Placed[] = [];
    // Tábuas do mesmo grupo lado a lado, quase paralelas, como a maré as deixou.
    const spacing = 0.5 + 0.2 * u(8);
    for (let m = 0; m < members; m++) {
      const k = 16 + m * 8;
      const side = (m - (members - 1) / 2) * spacing;
      const along = m === 0 ? 0 : (u(k) - 0.5) * 0.6;
      const px = x + dcos(yaw) * side + dsin(yaw) * along;
      const pz = z - dsin(yaw) * side + dcos(yaw) * along;
      const at = side === 0 && along === 0 ? site : this.site(rule, px, pz);
      if (!at) continue;
      const scale = rule.scale[0] + (rule.scale[1] - rule.scale[0]) * u(k + 1);
      const length = rule.length ? rule.length[0] + (rule.length[1] - rule.length[0]) * u(k + 2) : undefined;
      const placed = this.place(rule, PIECE_INDEX.get(piece)!, px, pz, at, m === 0 ? yaw : yaw + (u(k + 3) - 0.5) * 0.24, scale, length);
      if (!placed) continue;
      out.push({ ...placed, sub, rank, biome: at.biome, tint: Math.floor(u(k + 4) * 256) });
    }
    return out;
  }

  /** Rumo da peça: à toa, ou ao longo da curva de nível (tronco), virado para um lado ou outro. */
  private heading(rule: GroundDetailRule, site: Site, a: number, b: number): number {
    if (rule.pose !== "nivel" || Math.hypot(site.gx, site.gz) < FLAT) return a * TAU;
    // O eixo z da peça ao longo da curva de nível: perpendicular ao gradiente, com ±0,2 rad de folga.
    return datan2(-site.gz, site.gx) + (b < 0.5 ? 0 : Math.PI) + (a - 0.5) * 0.4;
  }

  /**
   * Assenta a peça em (x, z) e confere a forma no chão (pontas acima da água e fora dos POIs, longe dos
   * recursos e dos enfeites que bloqueiam). Devolve a matriz e a forma, ou `undefined`.
   */
  private place(rule: GroundDetailRule, piece: number, x: number, z: number, site: Site, yaw: number, scale: number, length: number | undefined): (Shape & { piece: number; m: number[] }) | undefined {
    const info = DETAIL_PIECES[DETAIL_PIECE_IDS[piece]!];
    const [w, h, d] = info.size;
    const sx = scale;
    const sy = scale;
    const sz = length !== undefined ? length / d : scale;
    const dirX = dsin(yaw);
    const dirZ = dcos(yaw);
    const long = "long" in info && info.long;
    const flat = "flat" in info && info.flat;
    const half = long ? (d * sz) / 2 : 0;
    const shape: Shape = { ax: x - dirX * half, az: z - dirZ * half, bx: x + dirX * half, bz: z + dirZ * half, r: info.radius * sx };
    let y = site.y - rule.sink * h * sy;
    let fx = dirX;
    let fy = 0;
    let fz = dirZ;
    let ux = 0;
    let uy = 1;
    let uz = 0;
    if (long || flat) {
      // Pontas fora dos POIs; o chão sob a peça inteira decide a inclinação e a altura.
      if (long && (this.clearance.inside(shape.ax, shape.az) || this.clearance.inside(shape.bx, shape.bz))) return undefined;
      const seat = this.seat(rule, x, z, dirX, dirZ, flat ? 0.4 * w * sx : 0, long ? half : 0.4 * d * sz, h * sy, site);
      if (!seat) return undefined;
      y = seat.y;
      // Frente subindo `c` por metro; cima é a normal do plano ajustado (`b` pela direita, `c` pela frente).
      fy = seat.c;
      ux = -seat.c * dirX - seat.b * dirZ;
      uz = seat.b * dirX - seat.c * dirZ;
    } else if (rule.pose !== "em-pe") {
      const n = 1 / Math.sqrt(site.gx * site.gx + 1 + site.gz * site.gz);
      ux = -site.gx * n;
      uy = n;
      uz = -site.gz * n;
      if (!long) {
        // Rumo à toa projetado no plano do chão.
        fy = -(fx * ux + fz * uz) / uy;
      }
    }
    let len = Math.hypot(fx, fy, fz);
    fx /= len;
    fy /= len;
    fz /= len;
    // Normal ortogonalizada ao eixo: a peça encosta no chão pelo comprimento e pela largura.
    const dot = ux * fx + uy * fy + uz * fz;
    ux -= dot * fx;
    uy -= dot * fy;
    uz -= dot * fz;
    len = Math.hypot(ux, uy, uz);
    ux /= len;
    uy /= len;
    uz /= len;
    // Direita = cima × frente (base positiva, como a identidade).
    const rx = uy * fz - uz * fy;
    const ry = uz * fx - ux * fz;
    const rz = ux * fy - uy * fx;
    if (this.hitsResource(shape) || this.blocked(rule, shape)) return undefined;
    return { ...shape, piece, m: [rx * sx, ry * sx, rz * sx, 0, ux * sy, uy * sy, uz * sy, 0, fx * sz, fy * sz, fz * sz, 0, x, y, z, 1] };
  }

  /**
   * Assenta pela área (as compridas pelo eixo, a laje numa grade até 40 % da largura e do comprimento a
   * partir do meio): amostra o chão sob a peça, ajusta um plano por mínimos quadrados (a subida `b` pela
   * direita e `c` pela frente; nas compridas, `b` é o gradiente do centro) e prende a altura do fundo no
   * meio entre flutuar (até `DETAIL_MAX_FLOAT` acima do chão em todo ponto) e enterrar (pelo menos
   * `DETAIL_MIN_SHOW` da altura à vista em todo ponto), o mais perto do afundamento da regra. Água sob a
   * peça, ou uma quebra de declive que não cabe nos dois limites, e ela não assenta (`undefined`).
   */
  private seat(rule: GroundDetailRule, x: number, z: number, dirX: number, dirZ: number, halfU: number, halfV: number, height: number, site: Site): { y: number; b: number; c: number } | undefined {
    const rightX = dirZ;
    const rightZ = -dirX;
    const nu = halfU > 0 ? Math.max(2, Math.ceil((2 * halfU) / SEAT_STEP)) : 0;
    const nv = Math.max(2, Math.ceil((2 * halfV) / SEAT_STEP));
    const samples: [number, number, number][] = [];
    let sum = 0;
    let gu = 0;
    let gv = 0;
    let uu = 0;
    let vv = 0;
    for (let i = 0; i <= nu; i++) {
      const u = nu ? -halfU + (2 * halfU * i) / nu : 0;
      for (let j = 0; j <= nv; j++) {
        const v = -halfV + (2 * halfV * j) / nv;
        const g = sampleHeight(this.heightfield, x + rightX * u + dirX * v, z + rightZ * u + dirZ * v);
        if (g < rule.minY) return undefined;
        samples.push([u, v, g]);
        sum += g;
        gu += u * g;
        gv += v * g;
        uu += u * u;
        vv += v * v;
      }
    }
    // A grade é simétrica em volta do meio: as equações normais se separam.
    const a = sum / samples.length;
    const b = uu > 0 ? gu / uu : site.gx * rightX + site.gz * rightZ;
    const c = gv / vv;
    let below = Infinity;
    let above = -Infinity;
    for (const [u, v, g] of samples) {
      const e = g - (a + b * u + c * v);
      below = Math.min(below, e);
      above = Math.max(above, e);
    }
    const lowest = a + above - (1 - DETAIL_MIN_SHOW) * height;
    const highest = a + below + DETAIL_MAX_FLOAT;
    if (lowest > highest) return undefined;
    return { y: Math.min(Math.max(a - rule.sink * height, lowest), highest), b, c };
  }

  /** A forma encosta no corpo de algum recurso (chunks vizinhos incluídos)? */
  private hitsResource(s: Shape): boolean {
    const reach = s.r + RESOURCE_MARGIN + MAX_BODY;
    const minX = Math.min(s.ax, s.bx) - reach;
    const maxX = Math.max(s.ax, s.bx) + reach;
    const minZ = Math.min(s.az, s.bz) - reach;
    const maxZ = Math.max(s.az, s.bz) + reach;
    this.ensureResources(minX, minZ, maxX, maxZ);
    for (let j = bucketOf(minZ); j <= bucketOf(maxZ); j++) {
      for (let i = bucketOf(minX); i <= bucketOf(maxX); i++) {
        for (const b of this.bodies.get(j * BODY_SIDE + i) ?? []) {
          const r = b.r + s.r + RESOURCE_MARGIN;
          if (pointSegment2(b.x, b.z, s) < r * r) return true;
        }
      }
    }
    return false;
  }

  /** Recursos (memorizados) dos chunks que cobrem o retângulo. */
  private ensureResources(minX: number, minZ: number, maxX: number, maxZ: number): void {
    const chunk = (v: number) => Math.min(CHUNKS_PER_SIDE - 1, Math.max(0, Math.floor((v + WORLD_HALF) / CHUNK_SIZE)));
    for (let cz = chunk(minZ); cz <= chunk(maxZ); cz++) {
      for (let cx = chunk(minX); cx <= chunk(maxX); cx++) {
        const key = cz * CHUNKS_PER_SIDE + cx;
        if (this.resourceChunks.has(key)) continue;
        this.resourceChunks.add(key);
        const set = this.generator.chunk(cx, cz);
        for (let k = 0; k < set.count; k++) {
          const x = set.x[k]!;
          const z = set.z[k]!;
          const bucket = bucketOf(z) * BODY_SIDE + bucketOf(x);
          const list = this.bodies.get(bucket) ?? [];
          list.push({ x, z, r: resourceBodyRadius(set.types[k]!, set.scale[k]!) });
          this.bodies.set(bucket, list);
        }
      }
    }
  }

  /** Alguma laje ou tronco dos vizinhos (de outra regra, ou de índice menor entre os que bloqueiam) na forma? */
  private blocked(rule: GroundDetailRule, s: Shape): boolean {
    for (const other of GROUND_DETAILS) {
      if (!BLOCKING.has(other.id) || other.index === rule.index) continue;
      if (BLOCKING.has(rule.id) && other.index > rule.index) continue;
      const reach = s.r + blockerReach(other);
      const c = other.cell;
      const i0 = Math.floor((Math.min(s.ax, s.bx) - reach + WORLD_HALF) / c);
      const i1 = Math.floor((Math.max(s.ax, s.bx) + reach + WORLD_HALF) / c);
      const j0 = Math.floor((Math.min(s.az, s.bz) - reach + WORLD_HALF) / c);
      const j1 = Math.floor((Math.max(s.az, s.bz) + reach + WORLD_HALF) / c);
      for (let j = j0; j <= j1; j++) {
        for (let i = i0; i <= i1; i++) {
          const b = this.blocker(other, i, j);
          if (b && touches(s, b)) return true;
        }
      }
    }
    return false;
  }
}

/** Maior alcance (m) da forma de uma regra a partir do candidato: meia peça comprida mais a meia largura, ou o raio. */
function blockerReach(rule: GroundDetailRule): number {
  let reach = 0;
  for (const piece of rule.pieces) {
    const info = DETAIL_PIECES[piece];
    const s = rule.scale[1];
    reach = Math.max(reach, "long" in info && info.long ? (rule.length?.[1] ?? info.size[2] * s) / 2 + info.radius * s : info.radius * s);
  }
  return reach;
}
